import { router } from '@inertiajs/react'
import { GemsView } from '@/components/gems'
import type { Gem, Status, Stream, PaginationInfo, GemFilterParams } from '@/components/gems'

interface Props {
  gems: Gem[]
  streams: Stream[]
  statuses: Status[]
  availableTags: string[]
  pagination: PaginationInfo
}

// Build URL with filter params
function buildUrl(path: string, filters: GemFilterParams, page?: number) {
  const params = new URLSearchParams()
  if (page && page > 1) params.set('page', String(page))
  if (filters.streamId) params.set('stream', filters.streamId)
  if (filters.sourceType) params.set('source', filters.sourceType)
  if (filters.statusId) params.set('status', filters.statusId)
  if (filters.tag) params.set('tag', filters.tag)
  if (filters.dateFrom) params.set('from', filters.dateFrom)
  if (filters.dateTo) params.set('to', filters.dateTo)

  const queryString = params.toString()
  return queryString ? `${path}?${queryString}` : path
}

export default function GemsIndex({ gems, streams, statuses, availableTags, pagination }: Props) {
  const handleViewItem = (itemId: string) => {
    router.visit(`/items/${itemId}`)
  }

  const handleViewGem = (gemId: string) => {
    // For now, navigate to the item detail
    const gem = gems.find(g => g.id === gemId)
    if (gem) {
      router.visit(`/items/${gem.itemId}`)
    }
  }

  const handleChangeStatus = (gemId: string, statusId: string) => {
    // Convert statusId from "new" to "new" or "in-progress" to "in_progress"
    const status = statusId.replace(/-/g, '_')
    router.patch(`/gems/${gemId}/update_status`, { status }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleUpdateTags = (gemId: string, tags: string[]) => {
    router.patch(`/gems/${gemId}/update_tags`, { tags }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleArchive = (gemId: string) => {
    if (confirm('Are you sure you want to archive this gem?')) {
      router.post(`/gems/${gemId}/archive`)
    }
  }

  const handleDelete = (gemId: string) => {
    if (confirm('Are you sure you want to delete this gem?')) {
      router.delete(`/gems/${gemId}`)
    }
  }

  const handleCreate = () => {
    // Navigate to items page to flag an item
    router.visit('/items')
  }

  const handleAddStatus = (name: string) => {
    // Status creation would require stream context
    // For account-level gems view, this is a no-op for now
    console.log('Add status:', name)
  }

  const handlePageChange = (page: number, filters: GemFilterParams) => {
    router.visit(buildUrl('/gems', filters, page), { preserveScroll: true })
  }

  const handleFilterChange = (filters: GemFilterParams) => {
    router.visit(buildUrl('/gems', filters), { preserveScroll: true })
  }

  return (
    <GemsView
      gems={gems}
      streams={streams}
      statuses={statuses}
      availableTags={availableTags}
      pagination={pagination}
      onViewItem={handleViewItem}
      onViewGem={handleViewGem}
      onChangeStatus={handleChangeStatus}
      onUpdateTags={handleUpdateTags}
      onArchive={handleArchive}
      onDelete={handleDelete}
      onCreate={handleCreate}
      onAddStatus={handleAddStatus}
      onPageChange={handlePageChange}
      onFilterChange={handleFilterChange}
    />
  )
}
